import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { toast } from '@/hooks/use-toast';
import { whatsappTemplatesApi } from '@/lib/api/whatsapp-templates.api';
import { extractErrorMessage } from '@/lib/utils';

import type {
  CreateWhatsAppTemplateDto,
  TemplateAccountResult,
  WhatsAppTemplateAccount,
} from '@/lib/api/whatsapp-templates.api';

const QUERY_KEY = ['whatsapp-templates'];
const ACCOUNTS_KEY = ['whatsapp-templates', 'accounts'];

const countStatus = (results: TemplateAccountResult[], status: TemplateAccountResult['status']) =>
  results.filter((r) => r.status === status).length;

/** "Created on 1, already linked on 0, failed on 0" plus the first failure message, if any. */
const describeAccountResults = (
  results: TemplateAccountResult[],
  parts: Array<[label: string, status: TemplateAccountResult['status']]>,
) => {
  const counts = parts.map(([label, status]) => `${label} on ${countStatus(results, status)}`);
  const summary = counts.join(', ').replace(/^./, (c) => c.toUpperCase());
  const firstError = results.find((r) => r.status === 'failed')?.error;
  return firstError ? `${summary}. ${firstError}` : summary;
};

const hasFailure = (results: TemplateAccountResult[]) => results.some((r) => r.status === 'failed');

export const useWhatsAppTemplates = () =>
  useQuery({ queryKey: QUERY_KEY, queryFn: () => whatsappTemplatesApi.list() });

export const useWhatsAppTemplateAccounts = () =>
  useQuery({ queryKey: ACCOUNTS_KEY, queryFn: () => whatsappTemplatesApi.listAccounts() });

export const useCreateWhatsAppTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateWhatsAppTemplateDto) => whatsappTemplatesApi.create(data),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      const failed = hasFailure(data.accounts);
      toast({
        variant: failed ? 'destructive' : 'success',
        title: failed ? 'Template created with errors' : 'Template created and submitted to Meta',
        description: describeAccountResults(data.accounts, [
          ['created', 'created'],
          ['already linked', 'linked'],
          ['failed', 'failed'],
        ]),
      });
    },
    onError: (error) =>
      toast({
        variant: 'destructive',
        title: 'Failed to create template',
        description: extractErrorMessage(error),
      }),
  });
};

export const useReplicateWhatsAppTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, wabaIds }: { id: string; wabaIds?: string[] }) =>
      whatsappTemplatesApi.replicate(id, wabaIds),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      const failed = hasFailure(data.accounts);
      toast({
        variant: failed ? 'destructive' : 'success',
        title: failed ? 'Template replicated with errors' : 'Template replicated',
        description: describeAccountResults(data.accounts, [
          ['created', 'created'],
          ['already linked', 'linked'],
          ['failed', 'failed'],
        ]),
      });
    },
    onError: (error) =>
      toast({
        variant: 'destructive',
        title: 'Failed to replicate template',
        description: extractErrorMessage(error),
      }),
  });
};

export const useSyncWhatsAppTemplates = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => whatsappTemplatesApi.sync(),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      const accounts = qc.getQueryData<WhatsAppTemplateAccount[]>(ACCOUNTS_KEY) ?? [];
      const perAccount = data.accounts
        .map((entry) => {
          const name = accounts.find((a) => a.waba_id === entry.waba_id)?.name;
          return `${name?.trim() || entry.waba_id}: ${entry.count}`;
        })
        .join(' · ');
      toast({
        variant: 'success',
        title: `Synced ${data.count} templates from Meta`,
        description: perAccount || undefined,
      });
    },
    onError: (error) =>
      toast({
        variant: 'destructive',
        title: 'Failed to sync templates',
        description: extractErrorMessage(error),
      }),
  });
};

export const useDeleteWhatsAppTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, wabaIds }: { id: string; wabaIds?: string[] }) =>
      whatsappTemplatesApi.delete(id, wabaIds),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY });
      const failed = hasFailure(data.accounts);
      toast({
        variant: failed ? 'destructive' : 'success',
        title: failed ? 'Template deleted with errors' : 'Template deleted',
        description: describeAccountResults(data.accounts, [
          ['deleted', 'deleted'],
          ['failed', 'failed'],
        ]),
      });
    },
    onError: (error) =>
      toast({
        variant: 'destructive',
        title: 'Failed to delete template',
        description: extractErrorMessage(error),
      }),
  });
};
